import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  workPermitTemplateApi,
  WorkPermitTemplateFormData,
} from 'shared/services/workPermitTemplateApi';
import { queryKeys } from '../queryConfig';

export interface TemplatesFilters {
  companyId?: string;
  includeGlobals?: boolean;
}

/**
 * Fetches templates for the given company (or globals when no company is set).
 * The queryKey always includes companyId so platform users switching across
 * client companies don't read stale lists from another tenant.
 */
export const useTemplates = (filters: TemplatesFilters = {}) => {
  const params: Record<string, unknown> = {
    limit: 1000,
    ...(filters.companyId && { companyId: filters.companyId }),
    ...(filters.companyId && { includeGlobals: filters.includeGlobals ?? true }),
  };

  // Always include companyId in the key (even when undefined) so the cache
  // entry for "globals only" is distinct from any per-tenant entry.
  const keyParams = { ...params, companyId: filters.companyId ?? null };

  return useQuery({
    queryKey: queryKeys.templates.list(keyParams),
    queryFn: async () => {
      const response = await workPermitTemplateApi.getAllTemplates(params as any);
      if (response.success && response.data) {
        return Array.isArray(response.data) ? response.data : [];
      }
      throw new Error(response.message || 'Error al cargar templates');
    },
    // Templates change rarely; bump staleTime well above the global 5 min
    // default so navigating between pages does not refetch the full list.
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};

/**
 * Fetches template categories.
 */
export const useTemplateCategories = () => {
  return useQuery({
    queryKey: queryKeys.templates.categories(),
    queryFn: async () => {
      const response = await workPermitTemplateApi.getCategories();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Error al cargar categorías');
    },
  });
};

/**
 * Mutation to create a template. Invalidates the templates cache on success.
 */
export const useCreateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: WorkPermitTemplateFormData) => {
      const response = await workPermitTemplateApi.createTemplate(data);
      if (response.success && response.data) return response.data;
      throw new Error(response.message || 'Error al crear el template');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
    },
  });
};

/**
 * Mutation to update a template. Invalidates the templates cache on success.
 */
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<WorkPermitTemplateFormData> }) => {
      const response = await workPermitTemplateApi.updateTemplate(id, data);
      if (response.success && response.data) return response.data;
      throw new Error(response.message || 'Error al actualizar el template');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
    },
  });
};

/**
 * Mutation to delete a template. Invalidates the templates cache on success.
 */
export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => workPermitTemplateApi.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
    },
  });
};

/**
 * Mutation to clone a template. Invalidates the templates cache on success.
 */
export const useCloneTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, newName }: { id: string; newName: string }) =>
      workPermitTemplateApi.cloneTemplate(id, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
    },
  });
};

/**
 * Mutation to toggle template active status. Invalidates the templates cache on success.
 */
export const useToggleTemplateStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => workPermitTemplateApi.toggleTemplateStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
    },
  });
};
